import { useMergeRefs } from '@floating-ui/react';
import { CSS, type Transform } from '@dnd-kit/utilities';
import { CSSProperties, Fragment, ReactElement, Ref } from 'react';
import { type DraggableSyntheticListeners, type UniqueIdentifier, useDroppable } from '@dnd-kit/core';
import {
  useSortable,
  SortableContext,
  rectSortingStrategy,
  type SortingStrategy,
  type AnimateLayoutChanges,
  defaultAnimateLayoutChanges,
} from '@dnd-kit/sortable';

import type { DocumentWithId } from '@/constants/types';
import { conditionalSpread } from '@flintmint/web-shared/utils/helper-utils';

export function SortableItem(props: any) {
  const { id, index, useDragOverlay: _useDragOverlay = true, renderItem, ...rest } = props;

  const animateLayoutChanges: AnimateLayoutChanges = (args) => defaultAnimateLayoutChanges({ ...args, wasDragging: true });

  const { attributes, isDragging, listeners, setNodeRef, transform, transition } = useSortable({
    id,
    animateLayoutChanges,
  });

  const style = {
    transition,
    ...conditionalSpread({ transform: CSS.Translate.toString(transform) }, !!transform),
  };

  return renderItem({
    style,
    index,
    transform,
    transition,
    ...listeners,
    ref: setNodeRef,
    dragging: isDragging,
    ...attributes,
    ...rest,
  });
}

export const SortableList = <T extends DocumentWithId>(props: SortableListProps<T>) => {
  const { id, ref, items, contextItems, className, strategy = rectSortingStrategy, renderItem } = props;

  const { setNodeRef } = useDroppable({
    id,
  });

  const sortableItems = contextItems || items;
  const sortableItemIds = new Set(sortableItems.map((item) => item.id));

  return (
    <>
      <SortableContext id={id} items={sortableItems.map((item) => item.id)} strategy={strategy}>
        <div ref={useMergeRefs([setNodeRef, ref])} className={className}>
          {items.map((item, index) => {
            if (sortableItemIds.has(item.id)) {
              return <SortableItem key={item.id} index={index} id={item.id} value={item} renderItem={renderItem} />;
            }
            // Render non-sortable item for measurement purposes
            return renderItem ? (
              <Fragment key={item.id}>{renderItem({ value: item, index, dragging: false, ...({} as any) })}</Fragment>
            ) : null;
          })}
        </div>
      </SortableContext>
    </>
  );
};

interface SortableListProps<T = DocumentWithId> {
  id: string;
  ref?: Ref<HTMLElement>;
  className?: string;
  items: T[];
  contextItems?: T[]; // Optional: items for SortableContext (defaults to items)
  activeId?: UniqueIdentifier | null;
  strategy?: SortingStrategy;
  renderItem?(args: {
    index?: number;
    dragging: boolean;
    dragOverlay?: boolean;
    ref?: Ref<HTMLElement>;
    listeners?: DraggableSyntheticListeners;
    style?: CSSProperties | undefined;
    transform?: Transform | null;
    transition?: string | undefined;
    value?: T;
  }): ReactElement;
}
